import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMe } from "@/lib/me";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { AnchorIntro } from "@/components/hte/AnchorIntro";
import { CandidatePanel } from "@/components/hte/CandidatePanel";
import { HteForm } from "@/components/hte/HteForm";
import { HteResult } from "@/components/hte/HteResult";
import { CadanganPanel } from "@/components/hte/CadanganPanel";
import { ModeToggle } from "@/components/hte/ModeToggle";
import { errMsg, isCancel, runJob, useApi } from "@/lib/api";

const toRef = (c) => ({ surah: c.ayat_ref.surah, from: c.ayat_ref.from, to: c.ayat_ref.to });

export default function HtePage() {
  const api = useApi();
  const me = useMe();
  const [mod, setMod] = useState("langit");
  const [input, setInput] = useState("");
  const [konteks, setKonteks] = useState("Umum");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [result, setResult] = useState(null);
  const [cards, setCards] = useState([]);
  const [seen, setSeen] = useState([]);
  const [isu, setIsu] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [ownFailed, setOwnFailed] = useState([]);
  const ctrl = useRef(null);
  const [params] = useSearchParams();
  const parent = params.get("parent") || null;

  useEffect(() => {
    const m = params.get("mod"), pIsu = params.get("isu"), ref = params.get("ref");
    if (m === "bumi" || m === "langit") setMod(m);
    if (m === "bumi" && pIsu) { setInput(pIsu); setIsu(pIsu); }
    if (params.get("konteks")) setKonteks(params.get("konteks"));
    if (ref && m !== "bumi") setInput(ref);
    if (ref && m === "bumi") {
      api("post", "/hte/user-refs", { refs: ref }).then((r) => {
        const kenapa = params.get("kenapa") || "Ayat kunci daripada kajian sebelumnya. Kaitan dengan isu akan dihuraikan semasa HTE dijana.";
        const cs = r.calon.map((c) => ({ ...c, sumber: "rantaian", kenapa }));
        setCards(cs); setSeen(cs.map(toRef));
      }).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const timers = useRef([]);

  const stage = (msgs) => {
    timers.current.forEach(clearTimeout);
    timers.current = msgs.map(([ms, m]) => setTimeout(() => setStatus(m), ms));
  };
  const begin = () => { ctrl.current = new AbortController(); setLoading(true); setError(""); return ctrl.current.signal; };
  const end = () => { timers.current.forEach(clearTimeout); setLoading(false); setStatus(""); };
  const cancel = () => { ctrl.current?.abort(); end(); toast("Dihentikan"); };
  const fail = (e) => { if (!isCancel(e)) setError(errMsg(e)); };

  const generate = async (ref, extra, signal) => {
    stage([[0, `Membaca 8 kitab tafsir untuk ${extra.rujukan}…`], [6000, "Menyusun lapis Q · Wahyu berdasarkan tafsir…"],
      [18000, "Menurunkan nilai, ilmu dan inovasi…"], [40000, "Hampir siap, menyemak rujukan ayat…"]]);
    const k = await runJob(api, "/hte/generate", { ...ref, konteks, ...extra }, signal);
    setResult(k);
    me?.refresh();
    setTimeout(() => document.querySelector("[data-testid='hte-result']")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const runLangit = async () => {
    const signal = begin();
    setResult(null); setWarning("");
    try {
      setStatus("Mengenal pasti rujukan ayat…");
      const p = await api("post", "/hte/parse", { input }, { signal });
      if (p.warning) setWarning(p.warning);
      await generate({ surah: p.surah, from: p.from, to: p.to }, { mod: "langit", input, rujukan: p.rujukan, parent_kajian_id: parent }, signal);
    } catch (e) { fail(e); } finally { end(); }
  };

  const searchCandidates = async (arahan = "", keepOwn = []) => {
    const signal = begin();
    stage([[0, "Mengenal pasti kata kunci isu…"], [4000, "Menyaring 6,236 ayat dalam pangkalan data…"], [9000, "Memilih 3 calon terbaik…"]]);
    try {
      const r = await runJob(api, "/hte/candidates", { isu: isu || input, konteks, arahan, exclude: seen }, signal);
      setCards([...r.calon, ...keepOwn]);
      setSeen((s) => [...s, ...r.calon.map(toRef)]);
    } catch (e) { fail(e); } finally { end(); }
  };

  const runBumi = async () => {
    const keep = cards.filter((c) => c.sumber === "rantaian");
    setResult(null); setSelectedId(null); setCards(keep); setSeen(keep.map(toRef)); setIsu(input.trim());
    const signal = begin();
    stage([[0, "Mengenal pasti kata kunci isu…"], [4000, "Menyaring 6,236 ayat dalam pangkalan data…"], [9000, "Memilih 3 calon terbaik…"]]);
    try {
      const r = await runJob(api, "/hte/candidates", { isu: input.trim(), konteks, exclude: keep.map(toRef) }, signal);
      setCards([...keep, ...r.calon]);
      setSeen([...keep, ...r.calon].map(toRef));
    } catch (e) { fail(e); } finally { end(); }
  };

  const more = (arahan) => searchCandidates(arahan, cards.filter((c) => c.sumber !== "ai"));

  const addOwn = async (refs) => {
    setBusy(true);
    try {
      const r = await api("post", "/hte/user-refs", { refs });
      setOwnFailed(r.gagal);
      const ids = new Set(cards.map((c) => c.id));
      setCards([...cards, ...r.calon.filter((c) => !ids.has(c.id))]);
      setSeen((s) => [...s, ...r.calon.map(toRef)]);
      if (r.calon.length) toast.success(`${r.calon.length} calon ditambah`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pick = async (c) => {
    setSelectedId(c.id);
    setResult(null);
    const signal = begin();
    try {
      await generate(toRef(c), { mod: "bumi", isu, input: isu, sendiri: c.sumber !== "ai", kenapa: c.kenapa, parent_kajian_id: parent, rujukan: c.rujukan,
        calon: cards.map(({ id, rujukan, kenapa, sumber, ayat_ref }) => ({ id, rujukan, kenapa, sumber, ayat_ref })) }, signal);
    } catch (e) { fail(e); } finally { end(); }
  };

  const switchMode = (m) => { setMod(m); setInput(""); setError(""); setWarning(""); };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-14 space-y-8">
      <header className="fade-up">
        <div className="eyebrow">Kaji Ayat al-Quran</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink mt-3">HTE <span className="text-mute">·</span> Heaven To Earth</h1>
        <p className="text-base text-mute mt-4 max-w-2xl">Dari Wahyu kepada Nilai, Ilmu, Inovasi dan Tindakan — Q → V → K → I. Mula dari ayat (Langit → Bumi) atau dari isu sebenar (Bumi → Langit → Bumi). Ada program latihan? <Link to="/program" className="text-q underline underline-offset-4" data-testid="hte-to-program-link">Saya Ada Program</Link>.</p>
      </header>
      {!result && cards.length === 0 && <AnchorIntro />}
      <ModeToggle mod={mod} onChange={switchMode} disabled={loading} />
      <HteForm mod={mod} input={input} setInput={setInput} konteks={konteks} setKonteks={setKonteks}
        loading={loading} status={status} onSubmit={mod === "langit" ? runLangit : runBumi} onCancel={cancel} />
      {error && <div className="rounded-xl p-4 bg-warn-soft text-warn text-sm flex gap-2" role="alert" data-testid="hte-error"><AlertTriangle size={16} className="shrink-0 mt-0.5" /><span>{error}{error.includes("Kuota carian") && <> <Link to="/akaun" className="underline font-semibold" data-testid="hte-error-akaun-link">Pergi ke Akaun</Link></>}</span></div>}
      {warning && <div className="rounded-xl p-4 bg-warn-soft text-warn text-sm flex gap-2" data-testid="hte-warning"><AlertTriangle size={16} className="shrink-0 mt-0.5" />{warning}</div>}
      {mod === "bumi" && cards.length > 0 && (
        <CandidatePanel cards={cards} selectedId={selectedId} onPick={pick} onMore={more} onOwn={addOwn}
          busy={busy || loading} ownFailed={ownFailed} picking={loading} />
      )}
      {result && <HteResult kajian={result} onUpdated={setResult} />}
      {result && <CadanganPanel key={result.id} kajian={result} auto />}
    </div>
  );
}
